import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, AsyncPipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AppSearchResultsService } from '../../../app-search-results.service';
import {
  IconBackComponent,
  IconClockComponent,
  IconFilterComponent,
} from '@pedix-workspace/angular-ui-icons';
import {
  CategoryAvailabilityDialogComponent,
  ItemListComponent,
  ItemListGroupProductsByCategoryPipe,
  ProductListItemComponent,
} from '@pedix-workspace/pedixapp-shared-catalog';
import { IsCategoryAvailablePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { EstablishmentsCheckoutService } from '../../order-checkout/establishments-checkout.service';
import { AppRequestContextService } from '../../../app-request-context.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { ApiV1_Product, ApiV1_ProductTag } from '@pedix-workspace/api-v1';
import { AppLayoutComponent } from '../../main/app-layout/app-layout.component';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { ProductFiltersDialogComponent } from '../category/product-filters/product-filters-dialog.component';
import { filter, switchMap } from 'rxjs';
import { getUniqueTagsWithQuantities } from '@pedix-workspace/pedixapp-shared-pipes';
import { StockIsOutOfStockPipe } from '@pedix-workspace/pedixapp-stock';
import { CartPromotionsService } from '../../../cart-promotions.service';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IconBackComponent,
    IconClockComponent,
    ButtonComponent,
    RouterLink,
    ProductListItemComponent,
    TranslocoDirective,
    IsCategoryAvailablePipe,
    AlertComponent,
    CategoryAvailabilityDialogComponent,
    ItemListComponent,
    ProductFiltersDialogComponent,
    IconFilterComponent,
    ItemListGroupProductsByCategoryPipe,
    StockIsOutOfStockPipe,
    AsyncPipe,
  ],
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private searchResultsService = inject(AppSearchResultsService);
  private appRequestContextService = inject(AppRequestContextService);
  private establishmentsCheckoutService = inject(EstablishmentsCheckoutService);
  readonly purchaseOptions = this.establishmentsCheckoutService.purchaseOptions;
  private appLayoutComponentRef = inject(AppLayoutComponent, { optional: true });
  private destroyRef = inject(DestroyRef);
  private cartPromotionsService = inject(CartPromotionsService);

  readonly filtersDialog = viewChild(ProductFiltersDialogComponent);

  readonly searchText = signal('');
  readonly searchTags = signal('');
  readonly selectedTagIds = signal<string[]>([]);

  private rawSearchResults = signal<ApiV1_Product[]>([]);

  readonly categories = computed(() => this.appRequestContextService.allCategories());
  readonly currencySymbol = computed(
    () => this.appRequestContextService.currencyLocaleData().currencySymbol,
  );
  readonly displaySku = computed(() => this.appRequestContextService.shouldDisplaySku());
  readonly searchResults = computed(() =>
    this.searchResultsService.getFilteredResults(this.rawSearchResults(), {
      productTagIds: this.selectedTagIds(),
    }),
  );
  readonly productTagsWithQuantities = computed<{ productTag: ApiV1_ProductTag; count: number }[]>(
    () => getUniqueTagsWithQuantities(this.rawSearchResults()),
  );

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(
        filter(queryParams => {
          if (!queryParams.q && !queryParams.qt) {
            this.router.navigate(['../'], { relativeTo: this.activatedRoute });
            return false;
          }
          this.selectedTagIds.set(queryParams.qt ? queryParams.qt.split(',') : []);
          return true;
        }),
        switchMap(queryParams => {
          this.searchText.set(queryParams.q);
          this.searchTags.set(queryParams.qt);

          return this.searchResultsService.getSearchResults(this.searchText(), {
            selectedLanguage: this.appRequestContextService.selectedLanguage(),
            displaySku: this.displaySku(),
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(searchResults => {
        this.rawSearchResults.set(searchResults);
      });
  }

  ngOnDestroy(): void {
    if (this.appLayoutComponentRef) {
      this.appLayoutComponentRef.searchText.set('');
    }
  }

  onFilterProducts() {
    this.filtersDialog()?.open();
  }

  onSelectedTags(selectedTags: string[]) {
    this.selectedTagIds.set(selectedTags);
  }

  getPromotionsForProduct(productId: string) {
    return this.cartPromotionsService.availablePromotionsByProductId().get(productId) ?? [];
  }
}
